import './components';
import { initGlobalUI } from './lib/ui/globalUi';

import { loadScript, PayPalNamespace } from '@paypal/paypal-js';
import { formatCurrency, formatPercent } from '../../src/commonMisc';
import { removeChildren, showAlert } from './lib/ui/uiUtils';
import { createSubscription, freeTrailSignUp, newSubscriptionApproved } from './lib/apiRequests';
import { detectIncognito } from 'detectincognitojs';

let paypal: PayPalNamespace | null = null;
loadScript({
	clientId: document.body.dataset.paypalClientId!,
	vault: true,
	intent: 'subscription',
})
	.then((result) => {
		paypal = result;

		const paymentContainer = document.querySelector<HTMLDivElement>(
			'#payment-container:not(.panel)'
		);
		if (paymentContainer) {
			const customPayPalPlan = paymentContainer.dataset.customPaypalPlan;

			loadPayPalButtons(
				'#paypal-button-container',
				paymentContainer.dataset.planId!,
				paymentContainer.dataset.quantity,
				customPayPalPlan
			);
		}
	})
	.catch((error) => {
		console.error('failed to load the PayPal JS SDK script', error);
	});

const loadPayPalButtons = (
	containerSelector: string,
	planId: string,
	quantity?: string,
	customPlanCode?: string,
	referralCode?: string
) => {
	if (paypal?.Buttons) {
		removeChildren(document.querySelector(containerSelector)!);
		paypal
			.Buttons({
				createSubscription: function () {
					return createSubscription(planId, quantity, customPlanCode, referralCode);
				},
				onApprove: async function (data) {
					if (data.subscriptionID && data.orderID) {
						console.log(
							`You have successfully created subscription ${data.subscriptionID} (order ${data.orderID})`
						);
						await newSubscriptionApproved(data.subscriptionID);
						window.location.href = '/payment-complete';
					}
				},
			})
			.render(containerSelector);
	}
};

const showPaymentPanel = (planId: string, planName: string, quantity?: string) => {
	const paymentContainer = document.querySelector<HTMLDivElement>('#payment-container')!;
	paymentContainer.classList.remove('hidden');
	document.querySelector('#backdrop')!.classList.remove('hidden');
	paymentContainer.querySelector('h3')!.innerText = planName;

	const referralCode = document.body.dataset.referralCode;

	detectIncognito().then((result) => {
		if (result.isPrivate && document.querySelector('.private-browser-warning') === null) {
			const privateBrowsingWarning = document.createElement('p');
			privateBrowsingWarning.classList.add('system-warning');
			privateBrowsingWarning.classList.add('private-browser-warning');
			privateBrowsingWarning.innerText = `PayPal checkout may not work correctly in private browsing / incognito mode. We recommend opening this page in a normal browser tab to complete your purchase.`;
			document.querySelector('#payment-container h4')!.before(privateBrowsingWarning);
		}
	});

	loadPayPalButtons('#paypal-button-container', planId, quantity, undefined, referralCode);
};

const individualButton = document.querySelector<HTMLButtonElement>('#purchase-individual');
individualButton?.addEventListener('click', () => {
	showPaymentPanel(individualButton.dataset.planId!, individualButton.dataset.planName!);
});

const facilitatorButton = document.querySelector<HTMLButtonElement>('#purchase-facilitator');
facilitatorButton?.addEventListener('click', () => {
	showPaymentPanel(facilitatorButton.dataset.planId!, facilitatorButton.dataset.planName!);
});

const teamButton = document.querySelector<HTMLButtonElement>('#purchase-team');
teamButton?.addEventListener('click', () => {
	showPaymentPanel(
		teamButton.dataset.planId!,
		teamButton.dataset.planName!,
		document.querySelector<HTMLSelectElement>('#team-members')!.value
	);
});
document.getElementById('team-members')?.addEventListener('change', (e: Event) => {
	const select = e.target! as HTMLSelectElement;

	const quantity = parseInt(select.value);
	const price = parseFloat(select.selectedOptions[0].dataset.price!);
	const discount = parseFloat(select.selectedOptions[0].dataset.discount!);
	document.querySelector('.plan.team .price > .price-figure')!.innerHTML = formatCurrency(price);
	document.querySelector('.team .total-figure')!.innerHTML = formatCurrency(price * quantity);
	if (discount) {
		document.querySelector('.team .savings-team .saving-figure')!.innerHTML =
			formatPercent(discount);
		document.querySelector('.team .savings-team')!.classList.remove('hidden');
		document.querySelector('.team .extra')?.classList.remove('hidden');
	} else {
		document.querySelector('.team .savings-team')!.classList.add('hidden');
		document.querySelector('.team .extra')?.classList.add('hidden');
	}
	// Update annual saving (if any)
	const annualSavingPercentSpan = document.querySelector<HTMLSpanElement>('.team .annual-saving')!;
	if (annualSavingPercentSpan) {
		const annualSavingPercentString = annualSavingPercentSpan.dataset.percent!;
		const annualSavingPercent = parseFloat(annualSavingPercentString);
		annualSavingPercentSpan.innerHTML = formatCurrency(
			(annualSavingPercent / 100) * price * quantity * 12
		);
	}
});

const studentMembersSelect = document.querySelector<HTMLSelectElement>('#student-members');
const teacherMembersSelect = document.querySelector<HTMLSelectElement>('#teacher-members');
if (studentMembersSelect && teacherMembersSelect) {
	const updateTotalPrice = () => {
		const studentQuantity = parseInt(studentMembersSelect.value);
		const studentPrice = parseFloat(studentMembersSelect.selectedOptions[0].dataset.price!);
		const teacherQuantity = parseInt(teacherMembersSelect.value);
		const teacherPrice = parseFloat(teacherMembersSelect.selectedOptions[0].dataset.price!);

		document.querySelector('.plan.educational .student-price-figure')!.innerHTML =
			formatCurrency(studentPrice);
		document.querySelector('.plan.educational .teacher-price-figure')!.innerHTML =
			formatCurrency(teacherPrice);

		const totalPrice = studentQuantity * studentPrice + teacherQuantity * teacherPrice;
		document.querySelector('.educational .total-figure')!.innerHTML = formatCurrency(totalPrice);
	};
	studentMembersSelect.addEventListener('change', updateTotalPrice);
	teacherMembersSelect.addEventListener('change', updateTotalPrice);
}

const trialButton = document.querySelector<HTMLButtonElement>('#trial-button');
trialButton?.addEventListener('click', () => {
	const paymentContainer = document.querySelector('#free-trial-container')!;
	paymentContainer.classList.remove('hidden');
	document.querySelector('#backdrop')!.classList.remove('hidden');

	const referralCode = document.body.dataset.referralCode;

	loadPayPalButtons(
		'#trial-button-container',
		trialButton.dataset.planId!,
		undefined,
		undefined,
		referralCode
	);
});

// Currently unused as we are using a PayPal subscription with free trial period instead
document.querySelector('#free-trial-sign-up')?.addEventListener('click', () => {
	const input = document.querySelector('#free-trial-container')!.querySelector('input')!;
	const emailAddress = input.value;
	if (!emailAddress || !input.validity.valid) {
		showAlert('Please enter a valid email address');
		return;
	}

	freeTrailSignUp(emailAddress).then((error) => {
		document.querySelector('#free-trial-container')!.classList.add('hidden');
		document.querySelector('#backdrop')!.classList.add('hidden');
		if (error) {
			showAlert(`${error}`);
		} else {
			showAlert(
				'Thank you for requesting a free trial. Please check your email for the sign up link.'
			);
		}
	});
});

const educationalButton = document.querySelector<HTMLButtonElement>('#purchase-educational');
educationalButton?.addEventListener('click', () => {
	const paymentContainer = document.querySelector<HTMLDivElement>('#payment-container')!;
	paymentContainer.classList.remove('hidden');
	document.querySelector('#backdrop')!.classList.remove('hidden');
	paymentContainer.querySelector('h3')!.innerText = 'Educational Plan';

	window.alert('Coming soon');
});

initGlobalUI();
